import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { preChatLabel, preChatTexts } from '../utils/preChatText';
import { Box, Button } from '@mui/material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import ScreenshotModal from "./screenshotModal.js";
import { isUrlTrainingHidden } from "../utils/webflowGate";
import '../translations.js';
import { findFontConfig, DEFAULT_FONT } from "../constants/fonts";

const WidgetPreview = ({ bot, widgetStyles, leadParameters = [], onScreenshotRefresh }) => {
  const { t, i18n } = useTranslation();
  const iframeRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const webflowAgent = isUrlTrainingHidden(
    localStorage.getItem("webflow_connected") === "1", bot?.webflow_connected, bot?.webflow_hosted
  );

  // Resolve the gate's required field ids → parameter objects, preserving the
  // selection order. Passed into the iframe so the pre-chat form mirrors what
  // visitors see when "Enforce User Details" is on.
  const preChatFields = (widgetStyles.requiredFields || [])
    .map((id) => leadParameters.find((p) => p.id === id))
    .filter(Boolean);

  useEffect(() => {
    if (!initialized || !iframeRef.current) return;

    const iframe = iframeRef.current;

    const updateZoomLevel = () => {
      if (!iframe.clientWidth || !iframe.clientHeight) return;

      let zoom;
      if (isMobilePage()) {
        zoom = 1;
      } else {
        const widthRatio = iframe.clientWidth / 1920;
        const heightRatio = iframe.clientHeight / 1440;
        zoom = (widthRatio + heightRatio);
      }
      setZoomLevel(zoom);
    };

    updateZoomLevel();
    window.addEventListener("resize", updateZoomLevel);
    return () => {
      window.removeEventListener("resize", updateZoomLevel);
    };
  }, [initialized]);

  useEffect(() => {
    if (!bot) return;

    let cancelled = false;
    const parser = new DOMParser();

    const loadStaticPreview = async () => {
      setLoading(true);
      setError(null);

      try {
        const screenshotSrc = isMobilePage()
          ? widgetStyles.websiteMobileScreenshotImgPath
          : widgetStyles.websiteDesktopScreenshotImgPath;

        // The widget (launcher icon + opened chat) must always render — whether or
        // not a site screenshot exists. With a screenshot it sits on top of the site
        // image; without one it sits on top of a full-bleed dashed placeholder. So we
        // ALWAYS write the iframe and inject the widget; only the backdrop changes.
        const hasScreenshot = !!screenshotSrc;
        const backdrop = hasScreenshot
          ? `<img src="${screenshotSrc}" alt="Site Screenshot" style="width:100%; height:100%; display:block; border-radius: 8px;" />`
          : `<div style="position:fixed; inset:0; z-index:0; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:14px; box-sizing:border-box; padding:20px; text-align:center; border:2px dashed #d0d0d0; border-radius:10px; background:#fafafa;">
               <svg viewBox="0 0 512 512" aria-hidden="true" style="width:34px; height:34px; fill:#bbbbbb;"><path d="M149.1 64.8 138.7 96H64C28.7 96 0 124.7 0 160V416c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64H373.3L362.9 64.8C356.4 45.2 338.1 32 317.4 32H194.6c-20.7 0-39 13.2-45.5 32.8zM256 192a96 96 0 1 1 0 192 96 96 0 1 1 0-192z"/></svg>
               <p style="margin:0; font-size:15px; color:#888888; font-weight:500;">${t('widget_no_screenshot_yet')}</p>
             </div>`;

        const staticHTML = `
          <!DOCTYPE html>
          <html>
            <head>
              <meta charset="utf-8">
            </head>
            <body style="margin:0; padding:0; box-sizing:border-box;">
              ${backdrop}
            </body>
          </html>
        `;

        const doc = parser.parseFromString(staticHTML, "text/html");

        injectWidget(doc, bot.id, zoomLevel);

        const mergedHTML = doc.documentElement.outerHTML;

        const iframe = iframeRef.current;
        if (!iframe) return;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;

        iframeDoc.open();
        iframeDoc.write(mergedHTML);
        iframeDoc.close();

        // The injected inline script wires up window.__widgetRefs on the
        // iframe's DOMContentLoaded. A programmatic document.write makes the
        // iframe 'load' event unreliable (it frequently never fires), which is
        // why the preview rendered blank most of the time. Poll briefly for the
        // refs instead of trusting iframe.onload, then style + mark initialized.
        let frames = 0;
        const applyWhenReady = () => {
          if (cancelled) return;
          const w = iframe.contentWindow;
          if (w && w.__widgetRefs && w.__widgetRefs.chatWindow) {
            applyWidgetStyles(iframeDoc, widgetStyles, preChatFields, i18n.language);
            setInitialized(true);
            return;
          }
          if (frames++ < 120) requestAnimationFrame(applyWhenReady);
        };
        requestAnimationFrame(applyWhenReady);
      } catch (err) {
        setError(t('widget_preview_load_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadStaticPreview();
    return () => { cancelled = true; };
  }, [bot, zoomLevel]);

  useEffect(() => {
    if (!initialized) return;
    if (!iframeRef.current) return;

    const iframeDoc = iframeRef.current.contentDocument;
    if (!iframeDoc) return;

    applyWidgetStyles(iframeDoc, widgetStyles, preChatFields, i18n.language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widgetStyles, leadParameters, initialized]);

  useEffect(() => {
  }, [zoomLevel]);

  const handleCaptured = (url) => {
    if (typeof onScreenshotRefresh === 'function') onScreenshotRefresh(url);
  };

  if (error) {
    return <div>{t('error')}: {error}</div>;
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <h3 style={{ margin: 0 }}>{t('widget_preview_title')}</h3>
        {/* A screenshot loads the page: not for agents on Webflow sites (the
            backend refuses it too, Webflow #1168480). */}
        {!webflowAgent && <Button
          variant="outlined"
          onClick={() => setModalOpen(true)}
          sx={{ padding: 0, paddingRight: '8px', paddingLeft: '8px', color: '#4b14b9', borderColor: '#4b14b9', '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' } }}
        >
          <FontAwesomeIcon icon={faCamera} style={{"marginRight": '8px'}}/>{t('widget_take_new_screenshot')}
        </Button>}
      </Box>

      {/* The iframe is ALWAYS mounted (never gated behind `loading`) so its ref
          is available when loadStaticPreview writes into it — gating it caused
          the write to be skipped and the preview to render blank. The spinner
          is an overlay while the first write/styling completes. */}
      <div
        style={{
          position: 'relative',
          flex: '1 1 auto',
          minHeight: 0,
          display: 'block',
        }}
      >
        <iframe
          ref={iframeRef}
          title={t('widget_preview_iframe_title')}
          style={{ width: "100%", height: "100%", border: "none", display: "block" }}
          sandbox="allow-scripts allow-same-origin"
        />
        {loading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(255, 255, 255, 0.65)',
            }}
          >
            <div className="spinner"></div>
          </div>
        )}
      </div>

      <ScreenshotModal
        open={modalOpen && !webflowAgent}
        onClose={() => setModalOpen(false)}
        defaultUrl={bot?.url || ''}
        onCaptured={handleCaptured}
        botId={bot.id}
      />
    </>
  );
};

export default WidgetPreview;

function isMobilePage() {
  if (typeof window !== "undefined") {
    return window.innerWidth <= 768;
  }
  return false;
}

function injectWidget(doc, botId, zoomLevel) {
  const { head, body } = doc;

  const noClickOverlay = doc.createElement("div");
  noClickOverlay.id = "no-click-overlay";
  body.appendChild(noClickOverlay);

  const chatContainer = doc.createElement("div");
  chatContainer.id = "chat-widget-container";
  chatContainer.setAttribute("data-user-id", botId);
  chatContainer.style.zoom = zoomLevel;
  body.appendChild(chatContainer);

  // marked comes bundled with the widget script below (no unversioned CDN copy).

  const previewStyle = doc.createElement("style");
  previewStyle.innerHTML = `
    :root {
      --widget-size: 80px;
    }

    body, html {
      margin: 0;
      padding: 0px;
      height: 100%;
      width: 100%;
      font-family: "DM Sans", sans-serif !important;
    }

    #chat-widget-container {
      position: fixed;
      z-index: 2147483647 !important;
      pointer-events: none;
      top: 0;
      left: 0;
      width: 0;
      height: 0;
      zoom: ${zoomLevel};
      font-family: "DM Sans", sans-serif !important;
    }

    .saicf-chat-window,
    .saicf-chat-widget-icon,
    .saicf-pop-up-container { pointer-events: auto; }

    .saicf-chat-window, .saicf-chat-window *,
    .saicf-chat-widget-icon, .saicf-chat-widget-icon *,
    .saicf-pop-up-container, .saicf-pop-up-container * {
      font-family: "DM Sans", sans-serif !important;
    }

    .saicf-chat-window p {
      line-height: 1.3 !important;
      font-size: 15px !important;
    }

    .saicf-chat-widget-icon {
      position: fixed;
      bottom: 20px;
      right: 20px;
      color: white;
      border-radius: 50%;
      width: 100px;
      height: 100px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      font-size: 36px;
      z-index: 999998 !important;
    }

    @keyframes pulse {
      0% { transform: scale(1); }
      50% { transform: scale(1.1); }
      100% { transform: scale(1); }
    }
    .pulsing {
      animation: pulse 2s infinite;
    }

    .saicf-chat-title {
      font-size: 18px;
      font-weight: 700;
    }

    .saicf-chat-window {
      position: fixed;
      bottom: 12px;
      right: 12px;
      width: 430px;
      height: 620px;
      background-color: transparent;
      border: none;
      border-radius: 16px;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
      display: flex !important;
      flex-direction: column;
      z-index: 2147483647 !important;
      opacity: 0;
      transform: translateY(100%);
      transition: opacity 0.5s ease, transform 0.5s ease;
    }
    .saicf-chat-window.show {
      opacity: 1;
      transform: translateY(0);
    }

    .hidden {
      display: none;
    }

    .saicf-chat-header {
      color: white;
      padding: 8px 12px;
      border-radius: 16px 16px 0px 0px;
      display: flex;
      flex-direction: column;
    }

    .saicf-chat-header-title {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .saicf-logo-message-container {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .saicf-close-btn {
      background: none;
      border: none;
      color: rgb(218, 43, 43);
      margin: 0;
      padding: 0;
      outline: none;
      cursor: pointer;
    }
    .saicf-close-chat-widget-icon {
      cursor: pointer;
      transition: opacity 0.4s ease, transform 0.4s ease;
    }
    .saicf-close-chat-widget-icon .fa-xmark,
    .saicf-close-chat-widget-icon svg {
      font-size: 1.6rem;
    }
    .saicf-close-chat-widget-icon:hover {
      transform: scale(1.2);
    }

    .saicf-chat-body {
      flex: 1;
      padding: 8px;
      padding-top: 0;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      background-color: white;
    }

    .saicf-chat-footer {
      display: flex;
      flex-direction: column;
      background-color: white;
      gap: 8px;
    }
    .saicf-powered-by {
      display: flex;
      justify-content: center;
    }
    .saicf-powered-by-text {
      font-size: 10px;
      color: rgb(146, 146, 146);
      cursor: pointer;
      text-decoration: underline;
    }
    .saicf-input-send-container {
      display: flex;
      border-top: 1px solid #f1f1f1;
      padding: 10px;
      background-color: #fafafa;
    }
    .saicf-chat-footer input {
      flex: 1 !important;
      padding: 7px !important;
      border: 1px solid #ccc !important;
      border-radius: 8px !important;
      outline: none !important;
    }
    .saicf-chat-footer button {
      display: flex;
      align-items: center;
      margin-left: 6px;
      padding-left: 15px;
      padding-right: 15px;
      color: white;
      border: none;
      border-radius: 10px;
      cursor: pointer;
      transition: background-color 0.4s ease, transform 0.2s ease;
    }
    .saicf-chat-footer button:hover {
      background-color: #0595d3;
      transform: translateY(-1.5px);
    }

    .saicf-widget-message {
      max-width: 80%;
      margin: 5px 0;
      padding: 5px;
      border-radius: 10px;
      display: inline-block;
    }
    .widget-user-message {
      align-self: flex-end !important;
      color: white !important;
      border-radius: 8px !important;
      padding: 8px !important;
      font-size: 16px !important;
    }
    .widget-bot-message {
      align-self: flex-start !important;
      background-color: #ffffff !important;
      color: rgb(43, 43, 43) !important;
      border-radius: 8px;
      font-size: 16px !important;
      max-width: calc(100% - 48px);
    }
    .saicf-message-row {
      display: flex;
      align-items: flex-start;
      gap: 1px;
      margin: 6px 0;
    }
    .saicf-message-row.user {
      justify-content: flex-end;
    }
    .saicf-message-avatar {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      background-size: cover;
      background-position: center;
      background-color: rgba(0, 0, 0, 0.05);
      border: 1px solid rgba(0, 0, 0, 0.08);
      flex-shrink: 0;
      margin-top: 4px;
    }
    .saicf-message-avatar.is-hidden {
      display: none;
    }
    .widget-bot-message strong {
      color: rgb(57, 57, 57);
    }
    .widget-bot-message ul li:not(:last-child) {
      margin-bottom: 6px;
    }
    .widget-user-message > :first-child,
    .widget-bot-message > :first-child {
      margin-top: 0;
    }
    .widget-user-message > :last-child,
    .widget-bot-message > :last-child {
      margin-bottom: 0;
    }

    .saicf-widget-send-icon {
      font-size: 18px;
      font-style: normal;
    }

    .saicf-loading-dots {
      display: flex;
      justify-content: left;
      align-items: center;
      height: 40px;
      margin-top: 10px;
      margin-bottom: 5px;
    }
    .saicf-loading-dots div {
      width: 8px;
      height: 8px;
      margin: 0 4px;
      border-radius: 50%;
      animation: loading 0.6s infinite alternate;
    }
    @keyframes loading {
      to {
        opacity: 0.3;
        transform: translateY(-8px);
      }
    }
    .saicf-loading-dots div:nth-child(2) {
      animation-delay: 0.2s;
    }
    .saicf-loading-dots div:nth-child(3) {
      animation-delay: 0.4s;
    }

    .saicf-chat-overlay {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 99998;
    }

    .saicf-chat-widget-icon.align-left {
      left: 25px !important;
      right: auto !important;
    }
    .saicf-chat-widget-icon.elevated {
      bottom: 70px !important;
    }
    .saicf-chat-window.align-left {
      left: 20px !important;
      right: auto !important;
    }
    .saicf-chat-window.elevated {
      bottom: 12px !important;
    }

    .saicf-widget-message table {
      border-collapse: separate;
      border-spacing: 0px;
      margin-top: 15px;
      margin-bottom: 15px;
      width: 100%;
      overflow: auto;
    }
    .saicf-widget-message th, .saicf-widget-message td {
      border: 1px solid rgba(0, 0, 0, 0.08);
      padding: .35rem .50rem;
      text-align: left;
      font-size: 14px;
    }
    .saicf-widget-message th {
      color: #000000;
      background-color: rgba(0, 0, 0, 0.06);
      border-right: 0;
      padding: .5rem .50rem;
      border-bottom: none;
    }
    .saicf-widget-message th:last-child {
      border-right: 1px solid rgba(0, 0, 0, 0.08);
    }
    .saicf-widget-message td {
      border-bottom: 0px solid rgba(0, 0, 0, 0.08);
      border-right: 0px solid rgba(0, 0, 0, 0.08);
    }
    .saicf-widget-message td:last-child {
      border-right: 1px solid rgba(0, 0, 0, 0.08);
    }
    .saicf-widget-message tr:first-child th:first-child {
      border-top-left-radius: 8px;
    }
    .saicf-widget-message tr:first-child th:last-child {
      border-top-right-radius: 8px;
    }
    .saicf-widget-message tr:last-child td:first-child {
      border-bottom-left-radius: 8px;
    }
    .saicf-widget-message tr:last-child td:last-child {
      border-bottom-right-radius: 8px;
    }
    .saicf-widget-message tr:last-child td {
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    .saicf-predefined-container{
      display:flex;
      flex-wrap:wrap-reverse;
      justify-content: flex-end;
      gap:8px;
      padding:6px 10px;
      padding-bottom: 0;
      background: white;
    }
    .saicf-predefined-container.hidden{display:none;}
    .saicf-predefined-container::-webkit-scrollbar{display:none;}

    .saicf-predefined-question{
      flex:0 0 auto;
      border: 1px solid #e8e8e8ff;
      background: transparent;
      color:#333;
      border-radius:20px;
      padding:6px 12px;
      font-size:14px;
      white-space:nowrap;
      cursor:pointer;
      transition:background-color .25s,transform .2s;
    }
    .saicf-predefined-question:hover{
      background: #f1f1f1ff;
      transform:translateY(-1px);
    }

    .saicf-send-message[disabled] {
      opacity: .5 !important;
      cursor: not-allowed !important;
    }
    /* Voice input: the microphone inside the input field on the right, as in the widget */
    .saicf-input-field {
      position: relative;
      flex: 1;
      display: flex;
      min-width: 0;
    }
    .saicf-input-field.has-mic input { padding-right: 34px !important; }
    .saicf-input-send-container .saicf-mic-btn {
      position: absolute;
      top: 0;
      bottom: 0;
      right: 2px;
      align-items: center;
      justify-content: center;
      width: 32px;
      padding: 0 !important;
      margin: 0 !important;
      background: transparent !important;
      color: #6b6b76 !important;
      transform: none !important;
      cursor: default;
    }
    .saicf-mic-btn svg { width: 20px; height: 20px; }
    .saicf-chat-footer input:disabled {
      background: #f5f5f5 !important;
      cursor: not-allowed !important;
    }
    .saicf-predefined-question.is-disabled {
      opacity: .5 !important;
      pointer-events: none !important;
    }

    .saicf-header-actions {
      display: flex;
      align-items: center;
      gap: 6px;
      position: relative;
    }
    .saicf-ellipsis-btn {
      background: none;
      border: none;
      color: inherit;
      padding: 4px;
      margin: 0;
      cursor: pointer;
      border-radius: 6px;
      transition: background-color .2s ease, transform .2s ease;
      font-size: 20px;
      margin-top: 4px;
    }
    .saicf-ellipsis-btn:hover { transform: scale(1.08); }
    .saicf-ellipsis-btn svg { width: 1.4em; height: 1.4em; display: block; }

    .saicf-menu {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      min-width: 160px;
      background: #fff;
      color: #1f2937;
      border: 1px solid rgba(0,0,0,.08);
      box-shadow: 0 8px 24px rgba(0,0,0,.15);
      border-radius: 10px;
      padding: 6px;
      z-index: 2147483647;
      opacity: 0;
      pointer-events: none;
      transform: translateY(-6px);
      transition: opacity .15s ease, transform .15s ease;
    }
    .saicf-menu.is-open {
      opacity: 1;
      pointer-events: auto;
      transform: translateY(0);
    }
    .saicf-menu-item {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border: 0;
      background: transparent;
      font: inherit;
      color: inherit;
      border-radius: 8px;
      cursor: pointer;
      text-align: left;
    }
    .saicf-menu-item:hover { background: #f3f4f6; }

    .saicf-menu-item svg { width: 1em; height: 1em; display: block; }

    @media (min-width: 769px) {
      .saicf-chat-window {
        width: min(430px, calc(100svw - 24px));
        height: min(620px, calc(100svh - 24px));
        max-height: calc(100svh - 24px);
      }
    }
  `;

  // Pre-chat form (Enforce User Details). Mirrors the real widget's
  // .saicf-pre-chat-* styles so the preview matches what visitors see.
  previewStyle.innerHTML += `
    .saicf-pre-chat-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: flex-start;
      padding: 30px 24px;
      flex: 1 1 0%;
      width: 100%;
      min-height: 0;
      text-align: center;
      background: #fff;
      overflow-y: auto;
      border-radius: 0 0 16px 16px;
      box-sizing: border-box;
    }
    .saicf-pre-chat-container.hidden { display: none; }
    .saicf-pre-chat-header { margin-bottom: 24px; flex-shrink: 0; }
    .saicf-pre-chat-icon { width: 48px; height: 48px; margin-bottom: 12px; color: #5616ea; }
    .saicf-pre-chat-header h3 { font-size: 20px; font-weight: 600; color: #333; margin: 0 0 6px 0; }
    .saicf-pre-chat-header p { font-size: 14px; color: #666; margin: 0; }
    .saicf-pre-chat-header .saicf-pre-chat-note { margin-top: 8px; font-size: 13px; color: #555; }
    .saicf-pre-chat-fields {
      width: 100%;
      max-width: 320px;
      display: flex;
      flex-direction: column;
      gap: 14px;
      margin-bottom: 20px;
    }
    .saicf-pre-chat-field { display: flex; flex-direction: column; text-align: left; }
    .saicf-pre-chat-field label {
      font-size: 13px;
      font-weight: 600;
      color: #444;
      margin-bottom: 5px;
    }
    .saicf-pre-chat-field input {
      padding: 11px 12px;
      border: 1px solid #ddd;
      border-radius: 8px;
      font-size: 15px;
      outline: none;
      font-family: inherit;
      box-sizing: border-box;
    }
    .saicf-pre-chat-field input::placeholder { color: #999; }
    .saicf-pre-chat-submit {
      background-color: #5616ea;
      color: white;
      border: none;
      border-radius: 8px;
      padding: 12px 28px;
      font-size: 15px;
      font-weight: 600;
      cursor: default;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      min-width: 140px;
      font-family: inherit;
    }
    .saicf-pre-chat-submit:disabled { background-color: #ccc; cursor: not-allowed; }
  `;

  if (isMobilePage()) {
    previewStyle.innerHTML += `
      .saicf-chat-overlay {
        display: none !important;
      }
      .saicf-chat-window {
        position: fixed !important;
        inset: 0 !important;
        width: 100% !important;
        height: 100% !important;
        border-radius: 0 !important;
        background: #ffffff !important;
        opacity: 0 !important;
        transform: translateY(100%) !important;
        transition: opacity 0.75s ease, transform 0.75s ease !important;
        display: flex !important;
        flex-direction: column !important;
      }
      .saicf-chat-window.show {
        opacity: 1 !important;
        transform: none !important;
      }
      .saicf-chat-window .saicf-chat-header {
        border-radius: 0 !important;
      }
      .saicf-chat-header {
        padding: 12px 10px;
        margin-bottom: 5px;
      }
      .saicf-chat-window .saicf-chat-body {
        flex: 1 1 auto !important;
        min-height: 0 !important;
        overflow-y: auto !important;
        -webkit-overflow-scrolling: touch !important;
      }
      .saicf-close-chat-widget-icon {
        font-size: 22px;
      }
      .saicf-chat-title {
        font-size: 18px;
        font-weight: 600;
      }
      .widget-user-message,
      .widget-bot-message {
        font-size: 17px;
      }
      .saicf-chat-widget-icon {
        width: 70px;
        height: 70px;
      }
      .saicf-pop-up-container {
        right: 20px;
        bottom: calc(30px + var(--widget-size));
      }
      .saicf-pop-up-container.align-left {
        left: 20px !important;
        right: auto !important;
      }
      .saicf-pop-up-message {
        padding: 7px 10px;
        font-size: 14px;
      }
    `;
  }

  head.appendChild(previewStyle);

  const inlineScript = doc.createElement("script");
  inlineScript.type = "text/javascript";
  inlineScript.text = `
    (function() {
      window.__widgetRefs = {
        welcomeMessages: ['Hello from inline script!'],
        avatarUrl: ''
      };

      function updateAvatarElement(el, url) {
        if (!el) return;
        const avatarUrl = typeof url === 'string' ? url : window.__widgetRefs.avatarUrl;
        if (avatarUrl) {
          el.style.backgroundImage = 'url(\"' + avatarUrl + '\")';
          el.classList.remove('is-hidden');
        } else {
          el.style.backgroundImage = '';
          el.classList.add('is-hidden');
        }
      }

      function renderTextToHTML(text) {
        var s = typeof text === 'string' ? text : String(text || '');
        s = s.replace(/\\\\n/g, '\\n');
        if (window.marked && typeof window.marked.parse === 'function') {
          window.marked.setOptions({ breaks: true });
          return window.marked.parse(s);
        } else {
          s = s.replace(/\\*\\*(.+?)\\*\\*/g, '<strong>$1<\\/strong>');
          s = '<p>' + s.replace(/\\n\\n/g, '</p><p>').replace(/\\n/g, '<br>') + '</p>';
          return s;
        }
      }

      document.addEventListener('DOMContentLoaded', initializeChatWidget);

      function initializeChatWidget() {
        const container = document.getElementById('chat-widget-container');
        if (!container) return;

        const chatWidgetIcon = document.createElement('div');
        chatWidgetIcon.className = 'saicf-chat-widget-icon';

        const chatOverlay = document.createElement('div');
        chatOverlay.className = 'saicf-chat-overlay hidden';

        const chatWindow = document.createElement('div');
        chatWindow.className = 'saicf-chat-window hidden';

        var headerHTML =
          '<div class="saicf-chat-header">' +
            '<div class="saicf-chat-header-title">' +
              '<div class="saicf-logo-message-container">' +
                '<span class="saicf-chat-title">Chat with us</span>' +
              '</div>' +
              '<div class="saicf-header-actions" aria-label="Chat actions">' +
                '<button class="saicf-ellipsis-btn" aria-label="More actions" title="More">' +
                  '<svg viewBox="0 0 448 512" fill="currentColor">' +
                    '<path d="M120 256a56 56 0 1 1-112 0 56 56 0 1 1 112 0zm160 0a56 56 0 1 1-112 0 56 56 0 1 1 112 0zm160 0a56 56 0 1 1-112 0 56 56 0 1 1 112 0z"/>' +
                  '</svg>' +
                '</button>' +
                '<button class="saicf-close-btn saicf-close-chat-widget-icon" aria-label="Close chat">' +
                  '<svg viewBox="0 0 384 512" style="height:32px;width:32px;fill:currentColor;">' +
                    '<path d="M310.6 361.4 233.3 284l77.3-77.3c12.5-12.5 12.5-32.8 0-45.3-12.5-12.5-32.8-12.5-45.3 0L188 238.7 110.7 161.4c-12.5-12.5-32.8-12.5-45.3 0-12.5 12.5-12.5 32.8 0 45.3l77.3 77.3-77.3 77.3c-12.5 12.5-12.5 32.8 0 45.3 12.5 12.5 32.8 12.5 45.3 0L188 327.3l77.3 77.3c12.5 12.5 32.8 12.5 45.3 0 12.5-12.5 12.5-32.8 0-45.3z"/>' +
                  '</svg>' +
                '</button>' +
                '<div class="saicf-menu" role="menu" aria-hidden="true">' +
                  '<button class="saicf-menu-item saicf-menu-item--clear" role="menuitem">' +
                    '<svg viewBox="0 0 448 512" fill="currentColor">' +
                      '<path d="M135.2 17.7c2.9-10.7 12.7-17.7 23.8-17.7h129.9c11.1 0 20.9 7.1 23.8 17.7L328 32H432c8.8 0 16 7.2 16 16s-7.2 16-16 16H416 32 16C7.2 64 0 56.8 0 48S7.2 32 16 32H120l15.2-14.3zM64 96H384l-21.2 355.9c-1.5 25.1-22.3 44.1-47.4 44.1H132.6c-25.1 0-45.9-19-47.4-44.1L64 96z"/>' +
                    '</svg>' +
                    'Clear chat' +
                  '</button>' +
                '</div>' +
              '</div>' +
            '</div>' +
          '</div>';

        var poweredByHTML =
          '<div class="saicf-powered-by">' +
            '<span class="saicf-powered-by-text" onclick="window.open(\\'https://portal.ultimo-bots.com\\', \\'_blank\\')">' +
              'Powered by Ultimo Bots' +
            '</span>' +
          '</div>';

        chatWindow.innerHTML =
          headerHTML +
          '<div class="saicf-pre-chat-container hidden">' +
            '<div class="saicf-pre-chat-header">' +
              '<svg class="saicf-pre-chat-icon" viewBox="0 0 640 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg">' +
                '<path d="M208 352c114.9 0 208-78.8 208-176S322.9 0 208 0S0 78.8 0 176c0 38.6 14.7 74.3 39.6 103.4c-3.5 9.4-8.7 17.7-14.2 24.7c-4.8 6.2-9.7 11-13.3 14.3c-1.8 1.6-3.3 2.9-4.3 3.7c-.5 .4-.9 .7-1.1 .8l-.2 .2 0 0 0 0C1 327.2-1.4 334.4 .8 340.9S9.1 352 16 352c21.8 0 43.8-5.6 62.1-12.5c9.2-3.5 17.8-7.4 25.3-11.4C134.1 343.3 169.8 352 208 352zM448 176c0 112.3-99.1 196.9-216.5 207C255.8 457.4 336.4 512 432 512c38.2 0 73.9-8.7 104.7-23.9c7.5 4 16 7.9 25.2 11.4c18.3 6.9 40.3 12.5 62.1 12.5c6.9 0 13.1-4.5 15.2-11.1c2.1-6.6-.2-13.8-5.8-17.9l0 0 0 0-.2-.2c-.2-.2-.6-.4-1.1-.8c-1-.8-2.5-2-4.3-3.7c-3.6-3.3-8.5-8.1-13.3-14.3c-5.5-7-10.7-15.4-14.2-24.7c24.9-29 39.6-64.7 39.6-103.4c0-92.8-84.9-168.9-192.6-175.5c.4 5.1 .6 10.3 .6 15.5z"/>' +
              '</svg>' +
              '<h3 class="saicf-pre-chat-title"></h3>' +
              '<p class="saicf-pre-chat-subtitle"></p>' +
              '<p class="saicf-pre-chat-note hidden"></p>' +
            '</div>' +
            '<div class="saicf-pre-chat-fields"></div>' +
            '<button class="saicf-pre-chat-submit" disabled></button>' +
          '</div>' +
          '<div class="saicf-chat-body"></div>' +
          '<div class="saicf-chat-footer">' +
            '<div class="saicf-predefined-container hidden"></div>' +
            poweredByHTML +
            '<div class="saicf-input-send-container">' +
              '<div class="saicf-input-field">' +
                '<input type="text" class="saicf-chat-input" placeholder="Type your message..."/>' +
                // Voice input: shown when the Design tab's switch is on (updated below with the other styles)
                '<button class="saicf-mic-btn" type="button" aria-hidden="true" tabindex="-1" style="display:none;">' +
                  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
                    '<rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3"/>' +
                  '</svg>' +
                '</button>' +
              '</div>' +
              '<button class="saicf-send-message" aria-label="Send message">' +
                '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" style="width: 16px; height: 16px; fill: currentColor;">' +
                  '<path d="M214.6 41.4c-12.5-12.5-32.8-12.5-45.3 0L7 203.6c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L160 140.3V464c0 17.7 14.3 32 32 32s32-14.3 32-32V140.3l107.6 108.7c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L214.6 41.4z"/>' +
                '</svg>' +
              '</button>' +
            '</div>' +
          '</div>';

        container.appendChild(chatWidgetIcon);
        container.appendChild(chatOverlay);
        container.appendChild(chatWindow);

        window.__widgetRefs.chatWidgetIcon = chatWidgetIcon;
        window.__widgetRefs.chatOverlay = chatOverlay;
        window.__widgetRefs.chatWindow = chatWindow;
        window.__widgetRefs.updateAvatarElements = function(url) {
          window.__widgetRefs.avatarUrl = url || '';
          const avatarNodes = chatWindow.querySelectorAll('.saicf-message-avatar');
          avatarNodes.forEach(function(node) {
            updateAvatarElement(node, window.__widgetRefs.avatarUrl);
          });
        };
        window.__widgetRefs.updateAvatarElements(window.__widgetRefs.avatarUrl);

        const menu = chatWindow.querySelector('.saicf-menu');
        const ellipsisBtn = chatWindow.querySelector('.saicf-ellipsis-btn');
        const actionsWrap = chatWindow.querySelector('.saicf-header-actions');
        const clearBtn = chatWindow.querySelector('.saicf-menu-item--clear');

        function toggleMenu(open) {
          const willOpen = typeof open === 'boolean' ? open : !menu.classList.contains('is-open');
          menu.classList.toggle('is-open', willOpen);
          menu.setAttribute('aria-hidden', String(!willOpen));
        }

        document.addEventListener('click', function(e) {
          if (!menu.classList.contains('is-open')) return;
          if (actionsWrap.contains(e.target)) return;
          toggleMenu(false);
        });

        document.addEventListener('keydown', function(e) {
          if (e.key === 'Escape' && menu.classList.contains('is-open')) toggleMenu(false);
        });

        if (ellipsisBtn) {
          ellipsisBtn.addEventListener('click', function(e) {
            e.stopPropagation();
            toggleMenu();
          });
        }

        if (clearBtn) {
          clearBtn.addEventListener('click', function(e) {
            e.preventDefault();
            toggleMenu(false);
            const chatBody = chatWindow.querySelector('.saicf-chat-body');
            chatBody.innerHTML = '';
            const loadingDots = chatBody.querySelector('.saicf-loading-dots');
            if (loadingDots) loadingDots.remove();

            const msgs = Array.isArray(window.__widgetRefs.welcomeMessages)
              ? window.__widgetRefs.welcomeMessages
              : [ "Hello! How can I help?" ];

            msgs.forEach(function(m){
              appendMessage(m, 'bot', true);
            });

            const chatInput = chatWindow.querySelector('.saicf-chat-input');
            if (chatInput) chatInput.focus();
          });
        }

        chatWidgetIcon.addEventListener('click', openChat);
        const closeBtn = chatWindow.querySelector('.saicf-close-btn');
        if (closeBtn) closeBtn.addEventListener('click', closeChat);

        function openChat() {
          chatWindow.classList.remove('hidden');
          forceReflow(chatWindow);
          chatWindow.classList.add('show');
          chatOverlay.classList.remove('hidden');
          if (window.innerWidth <= 768) {
            document.body.classList.add('no-scroll');
          }
          const chatBody = chatWindow.querySelector('.saicf-chat-body');
          if (chatBody && chatBody.childElementCount === 0) {
            const msgs = Array.isArray(window.__widgetRefs.welcomeMessages)
              ? window.__widgetRefs.welcomeMessages
              : [ "Hello! How can I help?" ];

            msgs.forEach(function(m){
              appendMessage(m, 'bot', true);
            });
          }
        }

        function closeChat() {
          chatWindow.classList.remove('show');
          chatOverlay.classList.add('hidden');
          if (window.innerWidth <= 768) {
            document.body.classList.remove('no-scroll');
          }
          setTimeout(function() { chatWindow.classList.add('hidden'); }, 300);
        }

        function forceReflow(el) {
          void el.offsetHeight;
        }

        const sendBtn = chatWindow.querySelector('.saicf-send-message');
        const chatInput = chatWindow.querySelector('.saicf-chat-input');
        if (sendBtn && chatInput) {
          sendBtn.addEventListener('click', sendMessage);
          chatInput.addEventListener('keypress', function(e) {
            if (e.key === 'Enter') sendMessage();
          });
        }

        function setBusy(b) {
          if (!sendBtn || !chatInput) return;
          chatInput.disabled = b;
          sendBtn.disabled = b;
          const chips = chatWindow.querySelectorAll('.saicf-predefined-question');
          chips.forEach(function(chip) {
            chip.classList.toggle('is-disabled', b);
            chip.tabIndex = b ? -1 : 0;
            chip.setAttribute('aria-disabled', String(b));
          });
        }

        function sendMessage() {
          const chatBody = chatWindow.querySelector('.saicf-chat-body');
          if (!chatBody) return;
          const text = chatInput.value.trim();
          if (text) {
            appendMessage(text, 'user', false);
            chatInput.value = '';
            setBusy(true);
            setLoading(true);
            setTimeout(function() {
              appendMessage("This is a preview of your bot reply!", 'bot', false);
              setLoading(false);
              setBusy(false);
            }, 500);
          }
        }

        function setLoading(isLoading) {
          const chatBody = chatWindow.querySelector('.saicf-chat-body');
          if (!chatBody) return;
          if (isLoading) {
            const loadingDots = document.createElement('div');
            loadingDots.className = 'saicf-loading-dots';
            loadingDots.innerHTML = '<div></div><div></div><div></div>';
            chatBody.appendChild(loadingDots);
            scrollToBottom(chatBody);
          } else {
            const loadingDots = chatBody.querySelector('.saicf-loading-dots');
            if (loadingDots) loadingDots.remove();
          }
        }

        function buildMessageRow(text, sender, isWelcome) {
          const row = document.createElement('div');
          row.className = 'saicf-message-row ' + sender;
          if (isWelcome) row.setAttribute('data-welcome', 'true');
          let avatarEl = null;
          if (sender === 'bot') {
            avatarEl = document.createElement('div');
            avatarEl.className = 'saicf-message-avatar';
            updateAvatarElement(avatarEl);
            row.appendChild(avatarEl);
          }
          const msgEl = document.createElement('div');
          msgEl.className = 'saicf-widget-message widget-' + sender + '-message';
          msgEl.innerHTML = renderTextToHTML(text);
          row.appendChild(msgEl);
          return row;
        }

        function appendMessage(text, sender, isWelcome, options) {
          const chatBody = chatWindow.querySelector('.saicf-chat-body');
          if (!chatBody) return null;
          const row = buildMessageRow(text, sender, isWelcome);
          const insertBefore =
            options && options.insertBefore && chatBody.contains(options.insertBefore)
              ? options.insertBefore
              : null;
          if (insertBefore) {
            chatBody.insertBefore(row, insertBefore);
          } else {
            chatBody.appendChild(row);
          }
          if (!(options && options.skipScroll)) {
            scrollToBottom(chatBody);
          }
          return row;
        }

        function refreshWelcome() {
          const chatBody = chatWindow.querySelector('.saicf-chat-body');
          if (!chatBody) return;
          const existing = chatBody.querySelectorAll('[data-welcome="true"]');
          existing.forEach(function(n){ n.remove(); });
          const msgs = Array.isArray(window.__widgetRefs.welcomeMessages)
            ? window.__widgetRefs.welcomeMessages
            : [ "Hello! How can I help?" ];
          const fragment = document.createDocumentFragment();
          msgs.forEach(function(m){
            fragment.appendChild(buildMessageRow(m, 'bot', true));
          });
          const firstNonWelcome = chatBody.querySelector(':scope > :not([data-welcome="true"])');
          if (firstNonWelcome) {
            chatBody.insertBefore(fragment, firstNonWelcome);
          } else {
            chatBody.appendChild(fragment);
          }
        }

        window.__widgetRefs.refreshWelcome = refreshWelcome;

        function scrollToBottom(el) {
          el.scrollTop = el.scrollHeight;
        }
      }
    })();
  `;
  body.appendChild(inlineScript);
}

function applyWidgetStyles(iframeDoc, widgetStyles, preChatFields = [], language = 'en') {
  const w = iframeDoc.defaultView;
  if (!w || !w.__widgetRefs) return;

  const { chatWidgetIcon, chatWindow } = w.__widgetRefs;
  if (!chatWidgetIcon || !chatWindow) return;

  const themeColor = widgetStyles.themeColor || "#0082ba";
  const hoverColor = widgetStyles.hoverColor || "#0595d3";
  const headerColor = widgetStyles.headerColor || "#ffffff";
  const widgetBorderRadius = widgetStyles.widgetBorderRadius ?? 50;
  const widgetSize = widgetStyles.widgetSize ?? 75;
  const logo = widgetStyles.logo || null;
  const icon = widgetStyles.icon || null;
  const avatar = widgetStyles.avatar || null;
  const horizontalAlignment = widgetStyles.widgetHorizontalAlignment || "right";
  const verticalAlignment = widgetStyles.widgetVerticalAlignment || "bottom";
  const chatTitle = widgetStyles.headerText || "Chat with us!";
  const inputPlaceholder = widgetStyles.inputPlaceholder || "Type your message...";
  const fontValue = widgetStyles.fontFamily?.trim() || DEFAULT_FONT.value;
  const fontConfig = findFontConfig(fontValue);
  const fontFamily = fontValue;
  const removePoweredBy = widgetStyles.removePoweredBy ?? false;
  const customBrandingText = (widgetStyles.customBrandingText || '').trim();
  let predefinedQuestions = widgetStyles.predefinedQuestions ?? [];

  let isPulsing = false;
  if (typeof widgetStyles.pulsing === "boolean") {
    isPulsing = widgetStyles.pulsing;
  } else if (typeof widgetStyles.pulsing === "string") {
    isPulsing = widgetStyles.pulsing.toLowerCase() === "true";
  }

  let welcomeMessages = Array.isArray(widgetStyles.welcomeMessages)
    ? widgetStyles.welcomeMessages
    : typeof widgetStyles.welcomeMessages === "string"
      ? [widgetStyles.welcomeMessages]
      : ["Welcome! How can I help?"];

  w.__widgetRefs.welcomeMessages = welcomeMessages;

  let dynamicStyles = iframeDoc.getElementById("widget-dynamic-styles");
  if (!dynamicStyles) {
    dynamicStyles = iframeDoc.createElement("style");
    dynamicStyles.id = "widget-dynamic-styles";
    iframeDoc.head.appendChild(dynamicStyles);
  }
  let fontLink = iframeDoc.getElementById("widget-font-link");
  if (fontConfig?.importUrl) {
    if (!fontLink) {
      fontLink = iframeDoc.createElement("link");
      fontLink.id = "widget-font-link";
      fontLink.rel = "stylesheet";
      iframeDoc.head.appendChild(fontLink);
    }
    if (fontLink.href !== fontConfig.importUrl) {
      fontLink.href = fontConfig.importUrl;
    }
  } else if (fontLink) {
    fontLink.remove();
  }

  dynamicStyles.innerHTML = `
    body,
    html,
    #chat-widget-container,
    .saicf-chat-window,
    .saicf-chat-window *,
    .saicf-chat-widget-icon,
    .saicf-chat-widget-icon *,
    .saicf-pop-up-container,
    .saicf-pop-up-container *,
    .saicf-widget-message,
    .saicf-widget-message * {
      font-family: ${fontFamily} !important;
    }
    .saicf-chat-footer button:hover {
      background-color: ${hoverColor} !important;
    }
    .widget-user-message {
      background-color: ${themeColor} !important;
    }
    .saicf-loading-dots div {
      background-color: ${themeColor} !important;
    }
  `;
  w.__widgetRefs.avatarUrl = avatar || "";

  const headerEl = chatWindow.querySelector(".saicf-chat-header");
  if (headerEl) {
    headerEl.style.backgroundColor = themeColor;
  }
  const titleEl = chatWindow.querySelector(".saicf-chat-title");
  if (titleEl) {
    titleEl.style.color = headerColor;
    titleEl.textContent = chatTitle;
  }

  const closeBtn = chatWindow.querySelector(".saicf-close-btn");
  if (closeBtn) {
    closeBtn.style.color = headerColor;
  }

  const ellipsisBtn = chatWindow.querySelector(".saicf-ellipsis-btn");
  if (ellipsisBtn) {
    ellipsisBtn.style.color = headerColor;
  }

  const inputField = chatWindow.querySelector(".saicf-chat-input");
  if (inputField) {
    inputField.placeholder = inputPlaceholder;
  }

  const sendBtn = chatWindow.querySelector(".saicf-send-message");
  if (sendBtn) {
    sendBtn.style.backgroundColor = themeColor;
    sendBtn.style.color = headerColor;
  }

  const micBtn = chatWindow.querySelector(".saicf-mic-btn");
  if (micBtn) {
    micBtn.style.display = widgetStyles.voiceInput ? "flex" : "none";
    micBtn.parentElement.classList.toggle("has-mic", !!widgetStyles.voiceInput);
  }

  if (icon) {
    chatWidgetIcon.innerHTML = `
      <img
        src="${icon}"
        alt="Widget Icon"
        style="max-width:100%; max-height:100%; border-radius:${widgetBorderRadius}%;"
      />
    `;
    chatWidgetIcon.style.backgroundColor = "transparent";
  } else {
    chatWidgetIcon.style.backgroundColor = themeColor;
    chatWidgetIcon.innerHTML = `
      <div style="display:flex; justify-content:center; align-items:center; background-color: transparent; padding-top: 3px">
        <svg xmlns="http://www.w3.org/2000/svg" width="65%" viewBox="0 0 24 24" fill="currentColor" class="size-6">
          <path fill-rule="evenodd" d="M12 2.25c-2.429 0-4.817.178-7.152.521C2.87 3.061 1.5 4.795 1.5 6.741v6.018c0 1.946 1.37 3.68 3.348 3.97.877.129 1.761.234 2.652.316V21a.75.75 0 0 0 1.28.53l4.184-4.183a.39.39 0 0 1 .266-.112c2.006-.05 3.982-.22 5.922-.506 1.978-.29 3.348-2.023 3.348-3.97V6.741c0-1.947-1.37-3.68-3.348-3.97A49.145 49.145 0 0 0 12 2.25ZM8.25 8.625a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Zm2.625 1.125a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z" clip-rule="evenodd" />
        </svg>
      </div>
    `;
  }

  if (widgetBorderRadius != null) {
    chatWidgetIcon.style.borderRadius = `${widgetBorderRadius}%`;
  }
  if (widgetSize != null) {
    chatWidgetIcon.style.height = `${widgetSize}px`;
    chatWidgetIcon.style.width = `${widgetSize}px`;
  }

  if (isPulsing) {
    chatWidgetIcon.classList.add("pulsing");
  } else {
    chatWidgetIcon.classList.remove("pulsing");
  }

  const logoMsgContainer = chatWindow.querySelector(".saicf-logo-message-container");
  if (logoMsgContainer) {
    const oldLogo = logoMsgContainer.querySelector("img");
    if (oldLogo) oldLogo.remove();
    if (logo) {
      const imgTag = iframeDoc.createElement("img");
      imgTag.src = logo;
      imgTag.alt = "Chat Logo";
      imgTag.style.height = "30px";
      imgTag.style.width = "30px";
      imgTag.style.borderRadius = "50%";
      imgTag.style.objectFit = "cover";
      const titleWithinHeader = logoMsgContainer.querySelector(".saicf-chat-title");
      if (titleWithinHeader) {
        logoMsgContainer.insertBefore(imgTag, titleWithinHeader);
      } else {
        logoMsgContainer.appendChild(imgTag);
      }
    }
  }

  if (typeof w.__widgetRefs.updateAvatarElements === "function") {
    w.__widgetRefs.updateAvatarElements(avatar);
  }

  const poweredByElement = chatWindow.querySelector(".saicf-powered-by");
  if (poweredByElement) {
    const poweredByTextEl = poweredByElement.querySelector(".saicf-powered-by-text");
    if (removePoweredBy) {
      if (customBrandingText) {
        if (poweredByTextEl) {
          poweredByTextEl.textContent = customBrandingText;
          poweredByTextEl.onclick = null;
          poweredByTextEl.style.cursor = 'default';
          poweredByTextEl.style.textDecoration = 'none';
        }
        poweredByElement.style.display = "flex";
      } else {
        poweredByElement.style.display = "none";
      }
    } else {
      if (poweredByTextEl) {
        poweredByTextEl.textContent = 'Powered by Ultimo Bots';
        poweredByTextEl.onclick = function () { window.open('https://portal.ultimo-bots.com', '_blank'); };
        poweredByTextEl.style.cursor = 'pointer';
        poweredByTextEl.style.textDecoration = '';
      }
      poweredByElement.style.display = "flex";
    }
  }

  ["align-left", "align-right", "elevated", "bottom"].forEach(cls => {
    chatWidgetIcon.classList.remove(cls);
    chatWindow.classList.remove(cls);
  });
  if (horizontalAlignment === "left") {
    chatWidgetIcon.classList.add("align-left");
    chatWindow.classList.add("align-left");
  } else {
    chatWidgetIcon.classList.add("align-right");
    chatWindow.classList.add("align-right");
  }
  if (verticalAlignment === "elevated") {
    chatWidgetIcon.classList.add("elevated");
    chatWindow.classList.add("elevated");
  } else {
    chatWidgetIcon.classList.add("bottom");
    chatWindow.classList.add("bottom");
  }

  const predefinedContainer = chatWindow.querySelector(".saicf-predefined-container");
  if (predefinedContainer) {
    predefinedContainer.innerHTML = "";
    if (typeof predefinedQuestions === "string") {
      try { predefinedQuestions = JSON.parse(predefinedQuestions); } catch {}
    }
    const hasQuestions =
      Array.isArray(predefinedQuestions) &&
      (predefinedQuestions.length > 1 ||
        (predefinedQuestions.length === 1 && (predefinedQuestions[0] || "").trim() !== ""));

    if (hasQuestions) {
      predefinedContainer.classList.remove("hidden");
      predefinedQuestions.forEach(q => {
        if (!q) return;
        const chip = iframeDoc.createElement("div");
        chip.className = "saicf-predefined-question";
        chip.textContent = q;
        chip.addEventListener("click", () => {
          const input = chatWindow.querySelector(".saicf-chat-input");
          const sendBtn = chatWindow.querySelector(".saicf-send-message");
          if (!input || !sendBtn) return;
          input.value = q;
          sendBtn.click();
        });
        predefinedContainer.appendChild(chip);
      });
    } else {
      predefinedContainer.classList.add("hidden");
    }
  }

  // ── Enforce User Details: pre-chat gate ────────────────────────────────
  // When the gate is on (and at least one field is required) the widget shows
  // the pre-chat form INSTEAD of the chat body/footer until the visitor fills
  // it in. Mirror that here so the preview reflects the visitor experience.
  const preChatContainer = chatWindow.querySelector(".saicf-pre-chat-container");
  const chatBodyEl = chatWindow.querySelector(".saicf-chat-body");
  const chatFooterEl = chatWindow.querySelector(".saicf-chat-footer");
  const showPreChat =
    !!widgetStyles.requirePreChat && Array.isArray(preChatFields) && preChatFields.length > 0;

  if (preChatContainer) {
    if (showPreChat) {
      preChatContainer.classList.remove("hidden");
      if (chatBodyEl) chatBodyEl.style.display = "none";
      if (chatFooterEl) chatFooterEl.style.display = "none";

      // The pre-chat bubble icon takes the widget's header (theme) colour.
      const preChatIcon = preChatContainer.querySelector(".saicf-pre-chat-icon");
      if (preChatIcon) preChatIcon.style.color = themeColor;

      // the form in the portal's language, as a visitor with that language sees it (utils/preChatText)
      const texts = preChatTexts(language);
      const setText = (sel, text) => { const el = preChatContainer.querySelector(sel); if (el) el.textContent = text; };
      setText(".saicf-pre-chat-title", texts.title);
      setText(".saicf-pre-chat-subtitle", texts.subtitle);
      setText(".saicf-pre-chat-note", texts.anyLanguage);
      setText(".saicf-pre-chat-submit", texts.start);
      const note = preChatContainer.querySelector(".saicf-pre-chat-note");
      if (note) note.classList.toggle("hidden", !widgetStyles.anyLanguageNote);

      const fieldsContainer = preChatContainer.querySelector(".saicf-pre-chat-fields");
      if (fieldsContainer) {
        fieldsContainer.innerHTML = "";
        preChatFields.forEach((field) => {
          const wrap = iframeDoc.createElement("div");
          wrap.className = "saicf-pre-chat-field";

          const label = iframeDoc.createElement("label");
          const fieldLabel = preChatLabel(field, language, widgetStyles.fieldLabels?.[field.id]);
          label.textContent = fieldLabel;

          const input = iframeDoc.createElement("input");
          const fname = (field.name || "").toLowerCase();
          input.type = fname.includes("email") ? "email" : "text";
          input.placeholder = fieldLabel;
          // Preview only — the form is non-interactive, so the Start Chat button
          // stays disabled exactly as a visitor first sees it.
          input.disabled = true;

          wrap.appendChild(label);
          wrap.appendChild(input);
          fieldsContainer.appendChild(wrap);
        });
      }
    } else {
      preChatContainer.classList.add("hidden");
      if (chatBodyEl) chatBodyEl.style.display = "";
      if (chatFooterEl) chatFooterEl.style.display = "";
    }
  }

  if (typeof w.__widgetRefs.refreshWelcome === "function") {
    w.__widgetRefs.refreshWelcome();
  }
}
